import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FiEdit2, FiTrash2, FiX, FiCamera,  FiDroplet, FiZap, FiCloudSnow, FiWind} from "react-icons/fi";
import { MdOutlineWater } from "react-icons/md";
import { TbAirConditioning } from "react-icons/tb";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { getMCUsByClientId } from "../api/mcuApi";
import QRScanner from "../components/QRScanner";
import {
  getControllerByMAC,
  createController,
  updateController,
  deleteController,
  scanControllerByMAC,
} from "../api/controllerApi";

const ControllerManager = () => {
  const { clientData } = useAuth();
  const [mcus, setMcus] = useState([]);
  const [controllers, setControllers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [editingController, setEditingController] = useState(null);
  const scannerRef = useRef();

  const controllerTypes = ["Humidifier", "Irrigation", "Ac", "Ventilation"];
const controllerIconMap = {
  Humidifier: FiDroplet,
  Irrigation: MdOutlineWater,
  Ac: TbAirConditioning,
  Ventilation: FiWind,
};
  useEffect(() => {
    const loadData = async () => {
      if (!clientData?.client_id) return;
      setLoading(true);
      try {
        const mcusResult = await getMCUsByClientId(clientData.client_id);
        const flatControllers = mcusResult.flatMap((mcu) =>
          (mcu.controllers || []).map((ctl) => ({
            ...ctl,
            mcu_id: mcu.mcu_id,
            mcu_name: mcu.name,
          }))
        );
        setMcus(mcusResult);
        setControllers(flatControllers);
      } catch (err) {
        toast.error(err.message || "Failed to load controllers");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [clientData]);

  const openModal = (unit = null) => {
    reset();
    setEditingController(unit);
    if (unit) {
      setValue("name", unit.name);
      setValue("mac", unit.mac);
      setValue("type", unit.type);
      setValue("mcu_id", unit.mcu_id);
    }
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  const openScanner = () => setShowScanner(true);
  const forceCloseScanner = () => {
    scannerRef.current?.stopCamera?.();
    setShowScanner(false);
  };

  const handleScan = async (mac) => {
    setShowScanner(false);
    setModalLoading(true);
    try {
      const result = await scanControllerByMAC(mac);
      openModal(result);
      reset({ name: result.name, mac: result.mac, type: result.type, mcu_id: result.mcu_id });
      toast.success(`Controller ${mac} loaded`);
    } catch {
      // openModal({ mac });
      reset({ name: "", mac, type: "" });
      toast.info(`No controller found for MAC: ${mac}`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirm delete?")) return;
    setDeleteLoading(id);
    try {
      await deleteController(id);
      toast.success("Deleted successfully");
      setControllers((s) => s.filter((c) => c.unit_id !== id));
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteLoading(null);
    }
  };

  const onSubmit = async (data) => {
    setModalLoading(true);
    try {
      if (editingController?.unit_id) {
        await updateController(editingController.unit_id, data);
        toast.success("Updated successfully");
      } else {
        await createController(data);
        toast.success("Created successfully");
      }
      setShowModal(false);
      // reload data
      const mcusResult = await getMCUsByClientId(clientData.client_id);
      const flat = mcusResult.flatMap((mcu) =>
        (mcu.controllers || []).map((ctl) => ({
          ...ctl,
          mcu_id: mcu.mcu_id,
          mcu_name: mcu.name,
        }))
      );
      setControllers(flat);
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setModalLoading(false);
    }
  };

  const filtered = controllers.filter(
    (c) =>
      (filterType === "All" || c.type === filterType) &&
      (c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.mac.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System Controllers</h1>
          <p className="text-gray-600">Manage your control units</p>
        </div>
        <button onClick={openScanner} className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
          <FiCamera className="mr-2" /> Scan Controller
        </button>
      </div>

      <div className="flex gap-4">
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or MAC..."
          className="flex-1 px-4 py-2 border rounded-lg"
        />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-4 py-2 border rounded-lg">
          <option>All</option>
          {controllerTypes.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p>Loading controllers…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {filtered.map((c) => {
  const Icon = controllerIconMap[c.type] || FiWind;
  return (
    <div key={c.unit_id} className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Icon className="text-xl text-green-600" />
          <div>
            <h3 className="font-semibold">{c.name}</h3>
            <p className="text-sm text-gray-500">{c.mac}</p>
          </div>
        </div>
                <div className="flex space-x-2">
                  <button onClick={() => openModal(c)} className="text-blue-600 hover:text-blue-800">
                    <FiEdit2 />
                  </button>
                  <button onClick={() => handleDelete(c.unit_id)} disabled={deleteLoading === c.unit_id} className="text-red-600 hover:text-red-800">
                    {deleteLoading === c.unit_id ? "Deleting…" : <FiTrash2 />}
                  </button>
                </div>
               </div>
      <div className="text-sm space-y-1">
        <p><strong>Type:</strong> {c.type}</p>
        <p><strong>MCU:</strong> {c.mcu_name}</p>
      </div>
    </div>
  );
})}
        </div>
      )}

      {showScanner && (
        <QRScanner
          ref={scannerRef}
          isOpen={showScanner}
          onClose={forceCloseScanner}
          onScan={handleScan}
          onError={() => toast.error("Scanner error")}
          title="Scan Controller QR"
          instructions="Position the QR code within the frame"
        />
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center">
          <div className="bg-white rounded-xl p-6 w-full max-w-md relative">
            <button onClick={closeModal} className="absolute top-4 right-4 text-gray-500 hover:text-red-600">
              <FiX />
            </button>
            <h2 className="text-lg font-bold mb-4">{editingController ? "Edit Controller" : "Add Controller"}</h2>

            {modalLoading ? (
              <p>Loading data…</p>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium">Name</label>
                  <input {...register("name", { required: "Name is required" })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium">MAC</label>
                  <p className="px-3 py-2 bg-gray-100 rounded-lg font-mono">{editingController ? editingController.mac : getValues("mac")}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium">Type</label>
                  <p className="px-3 py-2 bg-gray-100 rounded-lg">{editingController ? editingController.type : getValues("type")}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium">Assign to MCU</label>
                  <select {...register("mcu_id", { required: "Please select an MCU" })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select MCU</option>
                    {mcus.map((m) => (
                      <option key={m.mcu_id} value={m.mcu_id}>
                        {m.name} ({m.mac})
                      </option>
                    ))}
                  </select>
                  {errors.mcu_id && <p className="text-red-500 text-sm mt-1">{errors.mcu_id.message}</p>}
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    {editingController ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ControllerManager;
